import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    useTheme,
    useMediaQuery,
    Paper,
    alpha,
    Stack,
    Chip,
    Avatar,
    Divider,
    IconButton,
    Tooltip,
    Fade,
    Grow,
    Zoom as ZoomAnimation
} from '@mui/material';
import {
    Videocam as LiderVideoIcon,
    RecordVoiceOver as LiderRecordingIcon,
    Schedule as ScheduleIcon,
    People as PeopleIcon,
    Group as GroupIcon,
    Chat as ChatIcon,
    Workspaces as WorkspacesIcon,
    CalendarToday as CalendarIcon,
    CheckCircle as CheckCircleIcon,
    Mic as MicIcon,
    ClosedCaption as CaptionIcon,
    PresentToAll as PresentIcon,
    CallEnd as CallEndIcon,
    Security as SecurityIcon,
    Poll as PollIcon,
    Timer as TimerIcon,
    Star as StarIcon,
    Analytics as AnalyticsIcon,
    HighQuality as QualityIcon,
    Lock as LockIcon,
    Backup as BackupIcon,
    Email as EmailIcon,
    Folder as FolderIcon,
    TrendingUp as TrendingUpIcon,
    Speed as SpeedIcon,
    Language as LanguageIcon,
} from '@mui/icons-material';
import Navbar from '../Navbar';
import HomeFooter from '../HomeFooter';

const LiderConnectPage = () => {

    const [mounted, setMounted] = useState(false);
    const [meetingQuality, setMeetingQuality] = useState(92);

    useEffect(() => {
        setMounted(true);

        // Simulate meeting quality fluctuations
        const interval = setInterval(() => {
            setMeetingQuality(85 + Math.floor(Math.random() * 15));
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    // Lider Connect Core Features (Similar to Zoom)
    const liderConnectFeatures = [
        {
            category: "Video Conferencing",
            icon: <LiderVideoIcon sx={{ fontSize: 40 }} />,
            color: '#0d6efd',
            bgColor: '#e7f1ff',
            description: "Enterprise-grade video meetings with up to 1000 participants",
            features: [
                {
                    name: "HD Video & Audio",
                    details: "1080p HD video with crystal clear audio",
                    capability: "Up to 1000 participants",
                    icon: <QualityIcon />
                },
                {
                    name: "Screen Sharing",
                    details: "Share your screen with annotation tools",
                    capability: "4K support",
                    icon: <PresentIcon />
                },
                {
                    name: "Virtual Background",
                    details: "Custom backgrounds and blur effects",
                    capability: "AI-powered",
                    icon: <LiderVideoIcon />
                },
                {
                    name: "Recording",
                    details: "Cloud and local recording options",
                    capability: "Unlimited cloud storage",
                    icon: <LiderRecordingIcon />
                }
            ]
        },
        {
            category: "Team Collaboration",
            icon: <GroupIcon sx={{ fontSize: 40 }} />,
            color: '#198754',
            bgColor: '#d1e7dd',
            description: "Seamless team collaboration with integrated tools",
            features: [
                {
                    name: "Team Chat",
                    details: "Persistent chat channels and direct messaging",
                    capability: "Unlimited history",
                    icon: <ChatIcon />
                },
                {
                    name: "File Sharing",
                    details: "Share and collaborate on documents",
                    capability: "Up to 5GB per file",
                    icon: <FolderIcon />
                },
                {
                    name: "Calendar Integration",
                    details: "Sync with Google Calendar and Outlook",
                    capability: "Real-time sync",
                    icon: <CalendarIcon />
                },
                {
                    name: "Task Management",
                    details: "Create and assign tasks during meetings",
                    capability: "With reminders",
                    icon: <CheckCircleIcon />
                }
            ]
        },
        {
            category: "Lider Connect",
            icon: <StarIcon sx={{ fontSize: 40 }} />,
            color: '#087e86',
            bgColor: '#6068e0',
            description: "Advanced features for power users and enterprises",
            features: [
                {
                    name: "Breakout Rooms",
                    details: "Split meetings into smaller groups",
                    capability: "Up to 50 rooms",
                    icon: <WorkspacesIcon />
                },
                {
                    name: "Polling & Q&A",
                    details: "Interactive polls and audience questions",
                    capability: "Real-time results",
                    icon: <PollIcon />
                },
                {
                    name: "Live Transcription",
                    details: "Real-time captions in 30+ languages",
                    capability: "99% accuracy",
                    icon: <CaptionIcon />
                },
                {
                    name: "Analytics Dashboard",
                    details: "Meeting insights and participant engagement",
                    capability: "Detailed reports",
                    icon: <AnalyticsIcon />
                }
            ]
        }
    ];

    // Meeting Statistics
    const meetingStats = [
        {
            label: "Active Meetings",
            value: "2,847",
            change: "+12%",
            icon: <LiderVideoIcon />,
            color: '#0d6efd',
            subtext: "Right now"
        },
        {
            label: "Meeting Minutes",
            value: "125.4K",
            change: "+23%",
            icon: <TimerIcon />,
            color: '#198754',
            subtext: "This month"
        },
        {
            label: "Team Members",
            value: "15,892",
            change: "+18%",
            icon: <PeopleIcon />,
            color: '#ffc107',
            subtext: "Active users"
        },
        {
            label: "Recordings",
            value: "8,432",
            change: "+45%",
            icon: <LiderRecordingIcon />,
            color: '#dc3545',
            subtext: "Cloud stored"
        }
    ];

    // Live Meetings
    const liveMeetings = [
        {
            id: 1,
            title: "Weekly Team Sync",
            host: "Sarah Johnson",
            participants: 12,
            duration: "25 min",
            status: "live",
            type: "Team Meeting",
            color: '#0d6efd',
            participants_list: ["John D.", "Mike R.", "Lisa P.", "+9"]
        },
        {
            id: 2,
            title: "Client Presentation",
            host: "Michael Chen",
            participants: 8,
            duration: "15 min",
            status: "live",
            type: "Client Call",
            color: '#198754',
            participants_list: ["Emma W.", "David L.", "Robert K.", "+5"]
        },
        {
            id: 3,
            title: "Product Roadmap",
            host: "Alex Morgan",
            participants: 15,
            duration: "45 min",
            status: "upcoming",
            type: "Strategy",
            color: '#ffc107',
            participants_list: ["Tom H.", "Nina P.", "Chris B.", "+12"]
        },
        {
            id: 4,
            title: "Training Session",
            host: "Jessica Lee",
            participants: 25,
            duration: "60 min",
            status: "recorded",
            type: "Training",
            color: '#dc3545',
            participants_list: ["New hires", "Mentors"]
        }
    ];

    // Recent Meetings
    const recentMeetings = [
        {
            title: "Marketing Strategy",
            date: "Today, 10:30 AM",
            duration: "45 min",
            attendees: 8,
            recording: true,
            host: "You"
        },
        {
            title: "Development Sprint",
            date: "Yesterday, 2:00 PM",
            duration: "60 min",
            attendees: 12,
            recording: true,
            host: "Alex Chen"
        },
        {
            title: "Customer Success",
            date: "Mar 15, 2025",
            duration: "30 min",
            attendees: 5,
            recording: false,
            host: "Sarah Miller"
        },
        {
            title: "Product Demo",
            date: "Mar 14, 2025",
            duration: "50 min",
            attendees: 15,
            recording: true,
            host: "You"
        }
    ];

    // Integrations
    const integrations = [
        {
            name: "Google Calendar",
            icon: <CalendarIcon />,
            color: '#ea4335',
            description: "Schedule and join meetings directly from Calendar"
        },
        {
            name: "Microsoft Outlook",
            icon: <EmailIcon />,
            color: '#0078d4',
            description: "Sync meetings with Outlook calendar"
        },
        {
            name: "Slack",
            icon: <ChatIcon />,
            color: '#4a154b',
            description: "Get meeting notifications in Slack"
        },
        {
            name: "Dropbox",
            icon: <FolderIcon />,
            color: '#0061ff',
            description: "Save recordings to Dropbox"
        }
    ];

    // Security Features
    const securityFeatures = [
        {
            name: "End-to-end Encryption",
            icon: <LockIcon />,
            color: '#0d6efd',
            description: "All meetings are encrypted by default"
        },
        {
            name: "Waiting Rooms",
            icon: <PeopleIcon />,
            color: '#198754',
            description: "Control who joins your meetings"
        },
        {
            name: "Authentication",
            icon: <SecurityIcon />,
            color: '#ffc107',
            description: "SSO and 2FA support"
        },
        {
            name: "Data Backup",
            icon: <BackupIcon />,
            color: '#dc3545',
            description: "Automatic cloud backup"
        }
    ];

    if (!mounted) {
        return null;
    }

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            bgcolor: '#ffffff'
        }}>
            <Navbar />

            {/* Hero Section - Lider Connect Branding */}
            <Box sx={{
                background: 'linear-gradient(135deg, #f4f7fb 0%, #e2eaf4 100%)',
                pt: { xs: 8, md: 12 },
                pb: { xs: 12, md: 16 },
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Animated background elements */}
                <Box sx={{
                    position: 'absolute',
                    top: '5%',
                    right: '5%',
                    width: 400,
                    height: 400,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(0,0,0,0.03) 0%, transparent 70%)',
                    animation: 'pulse 4s ease-in-out infinite'
                }} />
                <Box sx={{
                    position: 'absolute',
                    bottom: '5%',
                    left: '5%',
                    width: 300,
                    height: 300,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(0,0,0,0.03) 0%, transparent 70%)',
                    animation: 'pulse 6s ease-in-out infinite'
                }} />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={6} alignItems="center">
                        {/* LEFT SIDE: Text Content */}
                        <Grid item xs={12} md={7}>
                            <Fade in timeout={800}>
                                <Box>
                                    <Stack
    direction="row"
    alignItems="center"
    spacing={{ xs: 1.5, md: 2 }}
    sx={{
        mb: 3,
        mt: 3,
        width: "100%",
        minWidth: 0,
    }}
>
    {/* Logo */}
    <Box
        component="img"
        src="/images/connect.png"
        alt="Lider Connect Logo"
        sx={{
            width: { xs: 70, md: 90 },
            height: { xs: 70, md: 90 },
            flexShrink: 0,
            objectFit: "contain",
            borderRadius: 2,
            bgcolor: "rgba(0, 0, 0, 0.05)",
            p: 1,
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(0, 0, 0, 0.1)",
        }}
    />

    {/* Chip */}
    <Chip
        label="✨ LIDER CONNECT - ALL-IN-ONE MEETING PLATFORM"
        sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: "100%",

            height: "auto",
            minHeight: { xs: 42, md: 40 },

            bgcolor: "rgba(0, 0, 0, 0.05)",
            color: "#1a1e2b",
            fontWeight: 700,
            fontSize: {
                xs: "0.72rem",
                sm: "0.78rem",
                md: "0.8rem",
            },

            backdropFilter: "blur(10px)",
            border: "1px solid rgba(0, 0, 0, 0.1)",

            "& .MuiChip-label": {
                display: "block",
                whiteSpace: {
                    xs: "normal",
                    md: "nowrap",
                },
                overflow: "hidden",
                textOverflow: {
                    xs: "unset",
                    md: "ellipsis",
                },
                overflowWrap: "anywhere",
                lineHeight: 1.4,
                textAlign: "left",
                py: { xs: 1, md: 0 },
                px: { xs: 1.2, md: 1.5 },
            },
        }}
    />
</Stack>

                                    <Typography
                                        variant="h1"
                                        sx={{
                                            fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                                            fontWeight: 800,
                                            mb: 2,
                                            lineHeight: 1.2,
                                            color: '#4551be', // Added your color here
                                        }}
                                    >
                                        Connect &  Collaborate
                                        <Box component="span" sx={{
                                            display: 'block',
                                            color: '#4551be', // Added your color here
                                        }}>
                                            Succeed with Lider Connect
                                        </Box>
                                    </Typography>

                                    <Typography
                                        variant="h5"
                                        sx={{
                                            mb: 4,
                                            color: '#4a5568',
                                            maxWidth: 600,
                                            fontWeight: 400,
                                            fontSize: { xs: '1.1rem', md: '1.3rem' }
                                        }}
                                    >
                                        Lider Connect brings powerful video meetings and seamless team
                                        collaboration together offering a complete communication solution
                                        for modern teams — all in one platform.
                                    </Typography>
                                </Box>
                            </Fade>
                        </Grid>

                        {/* RIGHT SIDE: Text & Stats based layout (No Image Mockup) */}
                        <Grid item xs={12} md={5}>
                            <Fade in timeout={1200}>
                                <Box sx={{ position: 'relative', display: { xs: 'none', md: 'block' } }}>
                                    {/* Main Feature Card */}
                                    <Paper sx={{
                                        p: 4,
                                        borderRadius: 4,
                                        boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                                        bgcolor: 'white',
                                        border: '1px solid rgba(0,0,0,0.05)',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}>
                                        {/* Decorative gradient line */}
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: 4,
                                            background: 'linear-gradient(90deg, #0d6efd 0%, #198754 100%)'
                                        }} />

                                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: '#1a1e2b' }}>
                                            Why Teams Choose Lider?
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6c757d', mb: 4 }}>
                                            Everything you need to connect, collaborate, and succeed.
                                        </Typography>

                                        {/* Feature List */}
                                        <Stack spacing={3}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Box sx={{
                                                    width: 48, height: 48, borderRadius: 2,
                                                    bgcolor: alpha('#0d6efd', 0.1),
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#0d6efd'
                                                }}>
                                                    <SpeedIcon />
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Ultra-Low Latency</Typography>
                                                    <Typography variant="caption" color="text.secondary">Real-time collaboration without delays.</Typography>
                                                </Box>
                                            </Stack>

                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Box sx={{
                                                    width: 48, height: 48, borderRadius: 2,
                                                    bgcolor: alpha('#198754', 0.1),
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#198754'
                                                }}>
                                                    <LanguageIcon />
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Global Reach</Typography>
                                                    <Typography variant="caption" color="text.secondary">Connect with teams across 150+ countries.</Typography>
                                                </Box>
                                            </Stack>

                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Box sx={{
                                                    width: 48, height: 48, borderRadius: 2,
                                                    bgcolor: alpha('#ffc107', 0.1),
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#ffc107'
                                                }}>
                                                    <TrendingUpIcon />
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>99.9% Uptime</Typography>
                                                    <Typography variant="caption" color="text.secondary">Reliable infrastructure you can trust.</Typography>
                                                </Box>
                                            </Stack>
                                        </Stack>

                                        <Divider sx={{ my: 3 }} />

                                    </Paper>

                                </Box>
                            </Fade>
                        </Grid>
                    </Grid>
                </Container>
            </Box>


            {/* Lider Connect Features - Zoom-like & Google Meet-like */}
            <Container maxWidth="xl" sx={{ py: 8 }}>
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Chip
                        label="⚡ ALL-IN-ONE MEETING PLATFORM"
                        sx={{
                            bgcolor: '#e7f1ff',
                            color: '#0d6efd',
                            fontWeight: 700,
                            mb: 2
                        }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase' }}>
                        Everything You Need in One Place
                    </Typography>
                </Box>

                <Grid container spacing={4} sx={{ mb: 8 }}>
                    {liderConnectFeatures.map((feature, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Grow in timeout={index * 200}>
                                <Card sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    overflow: 'visible',
                                    position: 'relative',
                                    transition: 'transform 0.3s',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 30px 60px rgba(0,0,0,0.12)'
                                    }
                                }}>
                                    <Box sx={{
                                        position: 'absolute',
                                        top: -20,
                                        left: 20,
                                        width: 64,
                                        height: 64,
                                        borderRadius: 2,
                                        bgcolor: feature.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        boxShadow: `0 10px 30px ${alpha(feature.color, 0.4)}`
                                    }}>
                                        {feature.icon}
                                    </Box>

                                    <CardContent sx={{ pt: 6, pb: 4 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#212529' }}>
                                            {feature.category}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6c757d', mb: 3 }}>
                                            {feature.description}
                                        </Typography>

                                        {feature.features.map((item, idx) => (
                                            <Box key={idx} sx={{ mb: 2 }}>
                                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0.5 }}>
                                                    <Box sx={{ color: feature.color, fontSize: 20 }}>
                                                        {item.icon}
                                                    </Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {item.name}
                                                    </Typography>
                                                    <Chip
                                                        label={item.capability}
                                                        size="small"
                                                        sx={{
                                                            ml: 'auto',
                                                            bgcolor: alpha(feature.color, 0.1),
                                                            color: feature.color,
                                                            fontSize: '0.65rem'
                                                        }}
                                                    />
                                                </Stack>
                                                <Typography variant="caption" sx={{ color: '#6c757d', display: 'block', ml: 5 }}>
                                                    {item.details}
                                                </Typography>
                                                {idx < feature.features.length - 1 && (
                                                    <Divider sx={{ my: 1.5 }} />
                                                )}
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grow>
                        </Grid>
                    ))}
                </Grid>

                {/* Live Meetings Section */}
                <Paper sx={{ p: 4, borderRadius: 3, bgcolor: '#f8f9fa', mb: 8 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#212529' }}>
                                Live Meetings
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Join active meetings happening right now
                            </Typography>
                        </Box>
                        <Chip
                            icon={<PeopleIcon />}
                            label="1,247 active participants"
                            sx={{ bgcolor: '#0d6efd', color: 'white' }}
                        />
                    </Box>

                    <Grid container spacing={3}>
                        {liveMeetings.map((meeting) => (
                            <Grid item xs={12} sm={6} key={meeting.id}>
                                <Card sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    borderLeft: `4px solid ${meeting.color}`,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        transform: 'translateX(4px)',
                                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                                    }
                                }}>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {meeting.title}
                                        </Typography>
                                        <Chip
                                            label={meeting.status.toUpperCase()}
                                            size="small"
                                            sx={{
                                                bgcolor: meeting.status === 'live' ? '#dc3545' :
                                                    meeting.status === 'upcoming' ? '#ffc107' : '#6c757d',
                                                color: 'white',
                                                height: 20,
                                                fontSize: '0.65rem'
                                            }}
                                        />
                                    </Stack>

                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                        Host: {meeting.host} • {meeting.type} • {meeting.duration}
                                    </Typography>

                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                        <PeopleIcon sx={{ fontSize: 16, color: '#6c757d' }} />
                                        <Typography variant="body2">
                                            {meeting.participants} participants
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" spacing={1}>
                                        {meeting.participants_list.map((p, idx) => (
                                            <Chip
                                                key={idx}
                                                label={p}
                                                size="small"
                                                sx={{ bgcolor: alpha(meeting.color, 0.1), color: meeting.color }}
                                            />
                                        ))}
                                    </Stack>

                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Recent Meetings */}
                <Grid container spacing={4} sx={{ mb: 8 }}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 4, borderRadius: 3, bgcolor: 'white' }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                                Recent Meetings
                            </Typography>

                            {recentMeetings.map((meeting, index) => (
                                <Box key={index}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                {meeting.title}
                                            </Typography>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Typography variant="caption" color="text.secondary">
                                                    {meeting.date}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    • {meeting.duration}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    • {meeting.attendees} attendees
                                                </Typography>
                                            </Stack>
                                        </Box>
                                        {meeting.recording && (
                                            <Tooltip title="Recording available">
                                                <IconButton size="small" sx={{ color: '#dc3545' }}>
                                                    <LiderRecordingIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                    {index < recentMeetings.length - 1 && <Divider sx={{ my: 2 }} />}
                                </Box>
                            ))}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 4, borderRadius: 3, bgcolor: 'white' }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                                Integrations
                            </Typography>

                            <Grid container spacing={2}>
                                {integrations.map((integration, index) => (
                                    <Grid item xs={6} key={index}>
                                        <Paper sx={{
                                            p: 2,
                                            textAlign: 'center',
                                            bgcolor: alpha(integration.color, 0.05),
                                            border: `1px solid ${alpha(integration.color, 0.2)}`,
                                            borderRadius: 2,
                                            transition: 'transform 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                bgcolor: alpha(integration.color, 0.1)
                                            }
                                        }}>
                                            <Box sx={{ color: integration.color, fontSize: 32, mb: 1 }}>
                                                {integration.icon}
                                            </Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                {integration.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {integration.description}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Security Features */}
                <Paper sx={{ p: 4, borderRadius: 3, bgcolor: '#f8f9fa', mb: 4 }}>
                    <Typography variant="h4" sx={{ textAlign: 'center', fontWeight: 700, mb: 4, textTransform: 'uppercase' }}>
                        Enterprise-Grade Security
                    </Typography>

                    <Grid container spacing={3}>
                        {securityFeatures.map((feature, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Stack alignItems="center" spacing={1}>
                                    <Box sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '50%',
                                        bgcolor: alpha(feature.color, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: feature.color
                                    }}>
                                        {feature.icon}
                                    </Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {feature.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ textAlign: 'center', color: '#6c757d' }}>
                                        {feature.description}
                                    </Typography>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

            </Container>

            <HomeFooter />

            {/* Global styles for animations */}
            <style jsx global>{`
                @keyframes pulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.2); opacity: 0.8; }
                }
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                }
            `}</style>
        </Box>
    );
};

export default LiderConnectPage;